import type {
  ActuacionesAU,
  Contribuccion,
  FileItem,
  IndicadoresAU,
  ProjectAU,
} from "@/types/types";
import type { GeoGeometryCollection } from "@/types/models/geometry";
import type {
  IndicadorEditableAU,
  ProjectAUFormPayload,
  ProjectAUTranslatableField,
} from "@/types/projects-au";
import type {
  ProjectsAUService,
  Refetch,
} from "@/lib/services/projects-au-service.interface";
import {
  ensureLanguageRows,
  SAVE_LANGS,
  saveAndAutoTranslateField,
} from "@/lib/languages";
import { removeFiles, splitNameExt, uploadFile } from "@/lib/file";
import {
  applyUserTranslationsAllTypes,
  applyUserTranslationsDeep,
  translateWithGPT,
  updateElement,
} from "@/lib/utils";
import type { ProjectsAURepository } from "@/lib/repositories/interfaces/projects-au-repository.interface";
import { directusProjectsAURepository } from "@/lib/repositories/projects-au.repository";

const PROJECT_TRANSLATABLE_FIELDS: ProjectAUTranslatableField[] = [
  "nombre",
  "descripcion",
  "beneficiarios",
  "observaciones",
  "actores",
  "potenciales_fuentes_financiacion",
  "area",
  "dimension",
];

const PROJECT_CHARACTERIZATION_FIELDS: ProjectAUTranslatableField[] = [
  "beneficiarios",
  "observaciones",
  "actores",
  "potenciales_fuentes_financiacion",
  "area",
];

const refresh = async (refetch?: Refetch) => {
  if (refetch) await Promise.resolve(refetch());
};

const normalizeNumeric = (value: unknown) => {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

type ContributionTipo = "oe" | "ods";

const isContribution = (value: unknown): value is Contribuccion =>
  Boolean(value && typeof value === "object" && "tipo" in value && "numero" in value);

const normalizeInt = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.trunc(numeric) : 0;
};

const normalizeNullableInt = (value: unknown) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.trunc(numeric) : null;
};

const normalizeContribution = (
  value: Partial<Contribuccion>,
  proyectoId?: number
): Contribuccion => {
  const tipo = String(value.tipo ?? "");

  return {
    ...value,
    tipo,
    proyecto_au: proyectoId ?? normalizeInt(value.proyecto_au),
    numero: normalizeInt(value.numero),
    porcentaje: tipo === "ods" ? 0 : normalizeNullableInt(value.porcentaje),
  };
};

const legacyContribucionesFromProject = (project: Partial<ProjectAU>) => [
  ...((project.oe ?? []).map((numero) => ({
    tipo: "oe",
    numero: normalizeInt(numero),
    porcentaje: 0,
    proyecto_au: project.id ?? 0,
  })) as Contribuccion[]),
  ...((project.ods ?? []).map((numero) => ({
    tipo: "ods",
    numero: normalizeInt(numero),
    porcentaje: 0,
    proyecto_au: project.id ?? 0,
  })) as Contribuccion[]),
];

const getProjectContribuciones = (project: Partial<ProjectAU> = {}) => {
  const oeContribuciones = Array.isArray(project.oe_contribuciones)
    ? project.oe_contribuciones.filter(isContribution)
    : [];
  const odsContribuciones = Array.isArray(project.ods_contribuciones)
    ? project.ods_contribuciones.filter(isContribution)
    : [];
  const contribuciones = [...oeContribuciones, ...odsContribuciones];

  return (contribuciones.length ? contribuciones : legacyContribucionesFromProject(project))
    .map((item) => normalizeContribution(item, project.id));
};

const groupContribucionesByProject = (contribuciones: Contribuccion[]) =>
  contribuciones.reduce((acc, item) => {
    const projectId =
      typeof item.proyecto_au === "object"
        ? Number(item.proyecto_au?.id)
        : Number(item.proyecto_au);
    if (!Number.isFinite(projectId)) return acc;
    const current = acc.get(projectId) ?? [];
    current.push(item);
    acc.set(projectId, current);
    return acc;
  }, new Map<number, Contribuccion[]>());

const hydrateProjectContribuciones = (
  project: ProjectAU,
  contribuciones: Contribuccion[]
): ProjectAU => {
  const normalized = contribuciones.map((item) =>
    normalizeContribution(item, project.id)
  );
  const idsByTipo = (tipo: ContributionTipo) =>
    normalized
      .filter((item) => item.tipo === tipo)
      .filter((item) => item.numero !== null && item.numero !== undefined)
      .map((item) => String(item.numero));

  return {
    ...project,
    oe: idsByTipo("oe"),
    ods: idsByTipo("ods"),
    oe_contribuciones: normalized.filter((item) => item.tipo === "oe"),
    ods_contribuciones: normalized.filter((item) => item.tipo === "ods"),
  };
};

const syncProjectContribuciones = async (args: {
  projectId: number;
  desired: Contribuccion[];
  existing?: Contribuccion[];
  repository: ProjectsAURepository;
}) => {
  const desired = args.desired.map((item) =>
    normalizeContribution(item, args.projectId)
  );
  const existing = (args.existing ?? []).map((item) =>
    normalizeContribution(item, args.projectId)
  );

  const matchedExistingIds = new Set<number>();
  const toCreate: Partial<Contribuccion>[] = [];

  for (const item of desired) {
    const existingItem =
      (item.id ? existing.find((current) => current.id === item.id) : undefined) ??
      existing.find(
        (current) =>
          !matchedExistingIds.has(current.id ?? -1) &&
          current.tipo === item.tipo &&
          current.numero === item.numero
      );

    if (existingItem?.id) {
      matchedExistingIds.add(existingItem.id);
      await args.repository.updateContribucion(existingItem.id, {
        tipo: item.tipo,
        proyecto_au: args.projectId,
        numero: item.numero,
        porcentaje: item.porcentaje ?? null,
      });
      continue;
    }

    toCreate.push({
      tipo: item.tipo,
      proyecto_au: args.projectId,
      numero: item.numero,
      porcentaje: item.porcentaje ?? null,
    });
  }

  if (toCreate.length) await args.repository.createContribuciones(toCreate);

  for (const item of existing) {
    if (item.id && !matchedExistingIds.has(item.id)) {
      await args.repository.deleteContribucion(item.id);
    }
  }
};

const mergeContribucionesForTipo = (
  current: Contribuccion[],
  tipo: ContributionTipo,
  nextIds: string[],
  nextContribuciones?: Contribuccion[],
  projectId?: number
) => {
  const other = current.filter((item) => item.tipo !== tipo);
  const source = nextContribuciones?.length
    ? nextContribuciones
    : nextIds.map((numero) => {
        const previous = current.find(
          (item) => item.tipo === tipo && item.numero === normalizeInt(numero)
        );
        return {
          ...previous,
          tipo,
          numero: normalizeInt(numero),
          porcentaje: previous?.porcentaje ?? null,
          proyecto_au: projectId ?? previous?.proyecto_au ?? 0,
        };
      });

  return [
    ...other,
    ...source
      .filter((item) => item.tipo === tipo)
      .map((item) => normalizeContribution(item, projectId)),
  ];
};

const createTranslationRows = (
  fields: readonly ProjectAUTranslatableField[],
  source: Record<string, unknown>,
  userLang: string
) =>
  ensureLanguageRows([], SAVE_LANGS).map((row) => ({
    ...row,
    ...fields.reduce((acc, field) => {
      acc[field] =
        row.lenguajes_code === userLang ? String(source[field] ?? "") : "";
      return acc;
    }, {} as Record<ProjectAUTranslatableField, string>),
  }));

const saveTranslatedField = (args: {
  field: string;
  value: string;
  userLang: string;
  element: unknown;
  tableName: string;
  refetch?: Refetch;
}) =>
  saveAndAutoTranslateField({
    ...args,
    refetch: args.refetch as any,
    updateElement,
    translateWithGPT,
  });

export const projectsAUService: ProjectsAUService = {
  async fetchProjectsByTenant(args: {
    tenantId: number;
    userLang: string;
    repository?: ProjectsAURepository;
  }): Promise<ProjectAU[]> {
    const repository = args.repository ?? directusProjectsAURepository;
    const data = await repository.fetchProjectsByTenant(args.tenantId);
    const localized = applyUserTranslationsDeep(data ?? [], args.userLang);
    const ids = localized
      .map((project) => Number(project.id))
      .filter((id) => Number.isFinite(id));
    const contribuciones = await repository.fetchContribucionesByProjects(ids);
    const contribucionesByProject = groupContribucionesByProject(contribuciones);

    return localized.map((project) =>
      hydrateProjectContribuciones(
        project,
        contribucionesByProject.get(Number(project.id)) ?? []
      )
    );
  },

  async fetchActuacionesByTenant(args: {
    tenantId: number;
    userLang: string;
    repository?: ProjectsAURepository;
  }): Promise<ActuacionesAU[]> {
    const repository = args.repository ?? directusProjectsAURepository;
    const data = await repository.fetchActuacionesByTenant(args.tenantId);
    return applyUserTranslationsAllTypes(data ?? [], args.userLang, ["nombre"]);
  },

  async fetchIndicadoresByTenant(args: {
    tenantId: number;
    userLang: string;
    repository?: ProjectsAURepository;
  }): Promise<IndicadoresAU[]> {
    const repository = args.repository ?? directusProjectsAURepository;
    const data = await repository.fetchIndicadoresByTenant(args.tenantId);
    return applyUserTranslationsAllTypes(data ?? [], args.userLang, [
      "descripcion",
    ]);
  },

  async deleteProject(args: {
    id: number;
    refetch?: Refetch;
    repository?: ProjectsAURepository;
  }): Promise<void> {
    const repository = args.repository ?? directusProjectsAURepository;
    await repository.deleteProject(args.id);
    await refresh(args.refetch);
  },

  async saveProjectForm(args: {
    isEditing: boolean;
    formData: ProjectAUFormPayload;
    initialData?: ProjectAU;
    userLang: string;
    currentTenantId?: string | number;
    refetchAfterSubmit: Refetch;
    repository?: ProjectsAURepository;
  }): Promise<void> {
    const repository = args.repository ?? directusProjectsAURepository;
    const { formData, userLang, currentTenantId, refetchAfterSubmit } = args;
    const proyectoId = formData.id;

    const basePayload: Partial<ProjectAU> = {
      codigo: (formData.codigo ?? "").trim(),
      estado: formData.estado,
      prioridad: formData.prioridad,
      horizonte_temporal: formData.horizonte_temporal,
      caracter: formData.caracter,
      tipo: formData.tipo,
      presupuesto_estimado: Number(formData.presupuesto_estimado) || 0,
      fecha_inicio: formData.fecha_inicio ?? null,
      fecha_fin: formData.fecha_fin ?? null,
      mapa: formData.mapa ?? null,
      tenant: currentTenantId ?? undefined,
      area_comarcal: formData.area_comarcal ?? null,
    } as Partial<ProjectAU>;

    const desiredContribuciones = getProjectContribuciones(formData);

    if (!args.isEditing || !proyectoId) {
      const created = await repository.createProject({
        ...basePayload,
        translations: createTranslationRows(
          PROJECT_TRANSLATABLE_FIELDS,
          formData,
          userLang
        ),
      });

      if (created?.id) {
        await syncProjectContribuciones({
          projectId: created.id,
          desired: desiredContribuciones,
          existing: [],
          repository,
        });
      }

      await refresh(refetchAfterSubmit);

      for (const field of PROJECT_TRANSLATABLE_FIELDS) {
        const value = String((formData as any)[field] ?? "").trim();
        if (!value || !created) continue;
        void saveTranslatedField({
          field,
          value,
          userLang,
          element: created,
          tableName: "proyectos_au_translations",
          refetch: refetchAfterSubmit,
        });
      }

      return;
    }

    await repository.updateProject(Number(proyectoId), basePayload);
    await syncProjectContribuciones({
      projectId: Number(proyectoId),
      desired: desiredContribuciones,
      existing: getProjectContribuciones(args.initialData),
      repository,
    });
    await refresh(refetchAfterSubmit);

    const changedFields = PROJECT_TRANSLATABLE_FIELDS.map((field) => {
      const next = String((formData as any)[field] ?? "").trim();
      const prev = String((args.initialData as any)?.[field] ?? "").trim();
      return { field, next, prev };
    }).filter(({ next, prev }) => next && next !== prev);

    for (const { field, next } of changedFields) {
      void saveTranslatedField({
        field,
        value: next,
        userLang,
        element: args.initialData,
        tableName: "proyectos_au_translations",
        refetch: refetchAfterSubmit,
      });
    }
  },

  async savePublicState(args: {
    id: number;
    publico: boolean;
    refetch?: Refetch;
    repository?: ProjectsAURepository;
  }) {
    const repository = args.repository ?? directusProjectsAURepository;
    await repository.updateProject(args.id, { publico: args.publico });
    await refresh(args.refetch);
  },

  async saveOds(args: {
    id: number | string;
    nextOds: string[];
    nextContribuciones?: Contribuccion[];
    refetch?: Refetch;
    repository?: ProjectsAURepository;
  }) {
    const repository = args.repository ?? directusProjectsAURepository;
    const current = await repository.fetchContribucionesByProject(Number(args.id));
    await syncProjectContribuciones({
      projectId: Number(args.id),
      desired: mergeContribucionesForTipo(
        current,
        "ods",
        args.nextOds,
        args.nextContribuciones,
        Number(args.id)
      ),
      existing: current,
      repository,
    });
    await refresh(args.refetch);
  },

  async saveOe(args: {
    id: string | number;
    nextOe: string[];
    nextContribuciones?: Contribuccion[];
    refetch?: Refetch;
    repository?: ProjectsAURepository;
  }) {
    const repository = args.repository ?? directusProjectsAURepository;
    const current = await repository.fetchContribucionesByProject(Number(args.id));
    await syncProjectContribuciones({
      projectId: Number(args.id),
      desired: mergeContribucionesForTipo(
        current,
        "oe",
        args.nextOe,
        args.nextContribuciones,
        Number(args.id)
      ),
      existing: current,
      repository,
    });
    await refresh(args.refetch);
  },

  async deleteActuacion(args: {
    actuacionId: number | string;
    refetch?: Refetch;
    repository?: ProjectsAURepository;
  }) {
    const repository = args.repository ?? directusProjectsAURepository;
    await repository.deleteActuacion(Number(args.actuacionId));
    await refresh(args.refetch);
  },

  async deleteIndicador(args: {
    indicadorId: number | string;
    refetch?: Refetch;
    repository?: ProjectsAURepository;
  }) {
    const repository = args.repository ?? directusProjectsAURepository;
    await repository.deleteIndicador(Number(args.indicadorId));
    await refresh(args.refetch);
  },

  async saveLocalizacion(args: {
    id: number | string;
    proyectoId?: number | null;
    folderId?: number;
    payload: {
      geometry: GeoGeometryCollection | null;
      imageFile: File | null;
      removeImage: boolean;
      initialImageUrl: string | null;
    };
    refetch?: Refetch;
    repository?: ProjectsAURepository;
  }): Promise<void> {
    if (!args.proyectoId) return;

    const repository = args.repository ?? directusProjectsAURepository;
    const { geometry, imageFile, removeImage, initialImageUrl } = args.payload;

    const removeInitialImage = async () => {
      if (initialImageUrl) await removeFiles([initialImageUrl]);
    };

    try {
      if (removeImage) await removeInitialImage();

      await repository.updateProject(Number(args.id), { mapa: geometry });

      if (!imageFile) return;

      await removeInitialImage();

      const res = await uploadFile(imageFile);
      const first = res?.[0];
      const directusFileId = (first &&
        (first.id ?? (first as any)?.data?.id)) as string | undefined;

      if (!directusFileId) {
        throw new Error("Directus upload did not return an id");
      }

      const { name, extension } = splitNameExt(imageFile.name);

      const payloads: Partial<FileItem>[] = [
        {
          name,
          extension,
          type: imageFile.type || "application/octet-stream",
          tags: "Principal",
          folder: args.folderId,
          file: directusFileId,
          date_last_modification: new Date().toISOString(),
        },
      ];

      await repository.createFileItems(payloads);
    } finally {
      await refresh(args.refetch);
    }
  },

  async saveActuacion(args: {
    nuevo: ActuacionesAU;
    proyectoId?: number | null;
    currentTenantId: number | string | null | undefined;
    refetch?: Refetch;
    userLang: string;
    repository?: ProjectsAURepository;
  }) {
    if (!args.proyectoId) return;

    const repository = args.repository ?? directusProjectsAURepository;
    const nombre = (args.nuevo.nombre ?? "").trim();

    const created = await repository.createActuacion({
      codigo: (args.nuevo.codigo ?? "").trim(),
      nombre: args.nuevo.nombre ?? "",
      presupuesto: Number(args.nuevo.presupuesto) || 0,
      proyecto: args.proyectoId,
      enlace: args.nuevo.enlace,
      tenant: args.currentTenantId,
      translations: ensureLanguageRows([], SAVE_LANGS).map((row) => ({
        ...row,
        nombre: row.lenguajes_code === args.userLang ? nombre : "",
      })),
    } as Partial<ActuacionesAU>);

    await refresh(args.refetch);

    if (!nombre || !created) return;

    await saveTranslatedField({
      field: "nombre",
      value: nombre,
      userLang: args.userLang,
      element: created,
      tableName: "actuaciones_translations",
      refetch: args.refetch,
    });
  },

  async updateActuacion(args: {
    actuacionId: string | number | undefined;
    proyectoId?: number | null;
    updated: ActuacionesAU;
    prevNombre?: string | null;
    currentActuacion?: ActuacionesAU | null;
    refetch?: Refetch;
    userLang: string;
    repository?: ProjectsAURepository;
  }) {
    if (!args.proyectoId || args.actuacionId == null) return;

    const repository = args.repository ?? directusProjectsAURepository;
    const nombre = (args.updated.nombre ?? "").trim();
    const prev = (args.prevNombre ?? "").trim();

    await repository.updateActuacion(Number(args.actuacionId), {
      codigo: (args.updated.codigo ?? "").trim(),
      nombre: args.updated.nombre ?? "",
      presupuesto: Number(args.updated.presupuesto) || 0,
      proyecto: args.proyectoId,
      enlace: args.updated.enlace,
    });

    await refresh(args.refetch);

    if (nombre && nombre !== prev) {
      await saveTranslatedField({
        field: "nombre",
        value: nombre,
        userLang: args.userLang,
        element:
          args.currentActuacion ??
          ({
            id: Number(args.actuacionId),
            ...args.updated,
          } as ActuacionesAU),
        tableName: "actuaciones_translations",
        refetch: args.refetch,
      });
    }
  },

  async saveIndicador(args: {
    nuevo: IndicadorEditableAU;
    proyectoId?: number | null;
    currentTenantId: number | string | null | undefined;
    refetch?: Refetch;
    userLang: string;
    repository?: ProjectsAURepository;
  }) {
    if (!args.proyectoId) return;

    const repository = args.repository ?? directusProjectsAURepository;
    const descripcion = (args.nuevo.descripcion ?? "").trim();
    const esNumerico = Boolean(args.nuevo.es_numerico);
    const valorOptimo = normalizeNumeric((args.nuevo as any)?.valor_optimo);

    const created = await repository.createIndicador({
      codigo: (args.nuevo.codigo ?? "").trim(),
      descripcion: args.nuevo.descripcion ?? "",
      unidad: args.nuevo.unidad ?? "",
      tipo: args.nuevo.tipo,
      proyecto: args.proyectoId,
      tenant: args.currentTenantId,
      es_numerico: esNumerico,
      valor_optimo: valorOptimo,
      estado: esNumerico ? "" : args.nuevo.estado ?? "",
      translations: ensureLanguageRows([], SAVE_LANGS).map((row) => ({
        ...row,
        descripcion:
          row.lenguajes_code === args.userLang ? descripcion : "",
      })),
    } as any);

    if (esNumerico && (args.nuevo.valores_aue?.length ?? 0) > 0 && created?.id != null) {
      const valoresPayload = (args.nuevo.valores_aue ?? [])
        .filter((row) => !row._deleted)
        .map((row) => ({
          ts: String(row.ts).trim(),
          valor: Number(row.valor),
          geometria: row.geometria,
          indicador: Number(created.id),
        }));

      await repository.createValores(valoresPayload);
    }

    await refresh(args.refetch);

    if (!descripcion || !created) return;

    await saveTranslatedField({
      field: "descripcion",
      value: descripcion,
      userLang: args.userLang,
      element: created,
      tableName: "indicadores_translations",
      refetch: args.refetch,
    });
  },

  async updateIndicador(args: {
    indicadorId: string | number | undefined;
    proyectoId?: number | null;
    updated: IndicadorEditableAU;
    prevDescripcion?: string | null;
    currentIndicador?: IndicadoresAU | null;
    refetch?: Refetch;
    userLang: string;
    repository?: ProjectsAURepository;
  }) {
    if (!args.proyectoId || args.indicadorId == null) return;

    const repository = args.repository ?? directusProjectsAURepository;
    const descripcion = (args.updated.descripcion ?? "").trim();
    const prev = (args.prevDescripcion ?? "").trim();
    const esNumerico = Boolean(args.updated.es_numerico);
    const valorOptimo = normalizeNumeric((args.updated as any)?.valor_optimo);
    const indicadorId = Number(args.indicadorId);

    await repository.updateIndicador(indicadorId, {
      codigo: (args.updated.codigo ?? "").trim(),
      descripcion: args.updated.descripcion ?? "",
      unidad: args.updated.unidad ?? "",
      tipo: args.updated.tipo,
      proyecto: args.proyectoId,
      es_numerico: esNumerico,
      valor_optimo: valorOptimo,
      estado: esNumerico ? "" : args.updated.estado ?? "",
    } as any);

    if (esNumerico) {
      for (const row of args.updated.valores_aue ?? []) {
        if (row.id && row._deleted) {
          await repository.deleteValor(Number(row.id));
          continue;
        }

        if (row.id) {
          await repository.updateValor(Number(row.id), {
            ts: String(row.ts).trim(),
            valor: Number(row.valor),
            geometria: row.geometria,
            indicador: indicadorId,
          });
          continue;
        }

        if (!row._deleted) {
          await repository.createValores([
            {
              ts: String(row.ts).trim(),
              valor: Number(row.valor),
              geometria: row.geometria,
              indicador: indicadorId,
            },
          ]);
        }
      }
    } else {
      const existingRows = (args.updated.valores_aue ?? []).filter(
        (row) => row.id != null
      );
      for (const row of existingRows) {
        await repository.deleteValor(Number(row.id));
      }
    }

    await refresh(args.refetch);

    if (descripcion && descripcion !== prev) {
      await saveTranslatedField({
        field: "descripcion",
        value: descripcion,
        userLang: args.userLang,
        element:
          args.currentIndicador ??
          ({
            id: indicadorId,
            ...args.updated,
          } as any),
        tableName: "indicadores_translations",
        refetch: args.refetch,
      });
    }
  },

  async saveCaracterizacion(args: {
    id: number | string | undefined | null;
    proyecto?: ProjectAU;
    partial: Partial<ProjectAU>;
    refetch?: Refetch;
    userLang: string;
    repository?: ProjectsAURepository;
  }): Promise<void> {
    if (args.id == null || !args.proyecto) return;

    const repository = args.repository ?? directusProjectsAURepository;
    const isDifferent = (a: unknown, b: unknown) => (a ?? "") !== (b ?? "");
    const hasAnyChange = Object.entries(args.partial).some(([key, value]) =>
      isDifferent((args.proyecto as any)[key], value)
    );

    if (!hasAnyChange) return;

    const translatableChanges = PROJECT_CHARACTERIZATION_FIELDS.filter((field) => {
      const nextValue = (args.partial as any)[field];
      return (
        typeof nextValue === "string" &&
        isDifferent((args.proyecto as any)[field], nextValue)
      );
    });

    const nonTranslatablePartial = Object.fromEntries(
      Object.entries(args.partial).filter(
        ([key]) =>
          !PROJECT_CHARACTERIZATION_FIELDS.includes(key as any) &&
          key !== "translations"
      )
    ) as Partial<ProjectAU>;

    if (Object.keys(nonTranslatablePartial).length) {
      await repository.updateProject(Number(args.id), nonTranslatablePartial);
      await refresh(args.refetch);
    }

    await Promise.all(
      translatableChanges.map((field) =>
        saveTranslatedField({
          field,
          value: String((args.partial as any)[field] ?? ""),
          userLang: args.userLang,
          element: args.proyecto,
          tableName: "proyectos_au_translations",
          refetch: args.refetch,
        })
      )
    );
  },
};
